// Audio player: sequential playback built from manual #score-dropdown <li> items.
// Features:
//  - Duration display after metadata loads.
//  - Per-track play/pause toggle button (accessible + keyboard).
// Relies solely on DOM (no manifest fetch).

import lottie from 'lottie-web';

const audioPlayer = {
  tracks: [], // { index, number, file, audio, li }
  currentIndex: -1,
  initialized: false,
  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.$scoreDropdown = document.getElementById('score-dropdown');
    if (!this.$scoreDropdown) return;
    this.$waveform = document.querySelector('.nav-score-wrapper .waveform');
    this.$lottiePlayer = this.$waveform ? this.$waveform.querySelector('.lottie-player') : null;
    if (this.$lottiePlayer) {
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: './lottie/WaveOff_opt.json',
      });
    }
    this.buildFromDOM();
    if (!this.tracks.length) return;
    // Bind click + keyboard activation + add play buttons
    this.tracks.forEach((track) => {
      const { li, index } = track;
      if (!li) return;
      // Insert play button if missing
      if (!li.querySelector('.play-toggle')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'play-toggle';
        btn.setAttribute('aria-label', 'Play track');
        btn.textContent = '▶';
        // Place before number if number div exists else at start
        const numberEl = li.querySelector('.number');
        if (numberEl) {
          numberEl.parentNode.insertBefore(btn, numberEl);
        } else {
          li.insertAdjacentElement('afterbegin', btn);
        }
      }
      const playBtn = li.querySelector('.play-toggle');

      const liHandler = (e) => {
        if (e.type === 'keydown' && !(e.key === 'Enter' || e.key === ' ')) return;
        if (e.type === 'keydown') e.preventDefault();
        this.toggleTrack(index);
      };
      li.addEventListener('click', liHandler);
      li.setAttribute('tabindex', '0');
      li.addEventListener('keydown', liHandler);

      const btnHandler = (e) => {
        e.stopPropagation();
        this.toggleTrack(index);
      };
      playBtn.addEventListener('click', btnHandler);
      playBtn.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          btnHandler(e);
        }
      });
    });

    // Waveform acts as a global play/pause toggle for current (or first) track
    if (this.$waveform) {
      const toggleFromWaveform = (e) => {
        e.preventDefault();
        if (this.currentIndex === -1) {
          // Start with first track
          this.playIndex(0);
        } else {
          // Toggle current track
          const t = this.tracks[this.currentIndex];
          if (!t) return;
          if (t.audio.paused) {
            t.audio.play().catch(()=>{});
            t.li && t.li.classList.add('playing');
          } else {
            t.audio.pause();
            t.li && t.li.classList.remove('playing');
          }
          this.updateButtons();
        }
      };
      this.$waveform.setAttribute('role', 'button');
      this.$waveform.setAttribute('tabindex', '0');
      this.$waveform.setAttribute('aria-label', 'Play/Pause score audio');
      this.$waveform.addEventListener('click', toggleFromWaveform);
      this.$waveform.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFromWaveform(e); }
      });
    }

    // Tab visibility pause/resume: remember if audio was actively playing and restore only in that case
    this._resumeOnVisibility = false;
    const handleVisibility = () => {
      if (document.hidden) {
        const idx = this.currentIndex;
        if (idx !== -1) {
          const t = this.tracks[idx];
          if (t && !t.audio.paused && !t.audio.ended) {
            this._resumeOnVisibility = true;
            try { t.audio.pause(); } catch(_){}
            t.li && t.li.classList.remove('playing');
            this.updateButtons();
            this.updateWaveformState();
          } else {
            this._resumeOnVisibility = false;
          }
        } else {
          this._resumeOnVisibility = false;
        }
      } else {
        if (this._resumeOnVisibility) {
          const idx = this.currentIndex;
          if (idx !== -1) {
            const t = this.tracks[idx];
            if (t && t.audio.paused && !t.audio.ended) {
              t.audio.play().catch(()=>{});
              t.li && t.li.classList.add('playing');
              this.updateButtons();
              this.updateWaveformState();
            }
          }
        }
        this._resumeOnVisibility = false; // one-shot resume
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Provide cleanup hook if needed later
    this._visibilityCleanup = () => document.removeEventListener('visibilitychange', handleVisibility);
  },

  buildFromDOM() {
    const listItems = Array.from(this.$scoreDropdown.querySelectorAll('ul li'));
    if (!listItems.length) return;
    this.tracks = listItems.map((li, idx) => {
      const number = parseInt(li.getAttribute('data-track'), 10) || idx + 1;
      const fileAttr = li.getAttribute('data-file');
      const filename = fileAttr || `${number}.mp3`;
  const file = `./audio/${filename}`;
      const audio = new Audio(file);
      audio.preload = 'metadata';
      audio.addEventListener('ended', () => this.playNext());
      audio.addEventListener('loadedmetadata', () => this.updateDuration(li, audio));
      audio.addEventListener('error', () => {
        console.warn('[audioPlayer] Failed to load audio src:', file);
      });
      audio.addEventListener('play', () => {
        // ensure correct li has playing class
        if (this.currentIndex === idx) {
          li.classList.add('playing');
          this.updateButtons();
          this.updateWaveformState();
        }
      });
      audio.addEventListener('pause', () => {
        if (this.currentIndex === idx) {
          li.classList.remove('playing');
          this.updateButtons();
          this.updateWaveformState();
        }
      });
      return { index: idx, number, file, audio, li };
    });
  },

  clearPlayingClass() {
    this.tracks.forEach(t => t.li && t.li.classList.remove('playing'));
  },

  stopCurrent() {
    if (this.currentIndex === -1) return;
    const cur = this.tracks[this.currentIndex];
    try {
      cur.audio.pause();
    } catch (e) { /* ignore */ }
  },

  playIndex(idx) {
    if (idx < 0 || idx >= this.tracks.length) return;
    // Stop current
    this.stopCurrent();
    this.currentIndex = idx;
    const track = this.tracks[idx];
    this.clearPlayingClass();
    track.li && track.li.classList.add('playing');
    track.audio.play().catch(err => {
      console.warn('[audioPlayer] Playback error:', err.message);
    });
    this.updateButtons();
    this.updateWaveformState();
  },

  playNext() {
    const next = this.currentIndex + 1;
    if (next < this.tracks.length) {
      this.playIndex(next);
    } else {
      this.clearPlayingClass();
      this.currentIndex = -1;
      this.updateButtons();
      this.updateWaveformState();
    }
  }
};

// Additional helper methods appended after object definition
audioPlayer.updateDuration = function(li, audio) {
  if (!li) return;
  const timeEl = li.querySelector('.time');
  if (!timeEl) return;
  if (!isFinite(audio.duration)) return;
  timeEl.textContent = this.formatTime(audio.duration);
};

audioPlayer.formatTime = function(totalSeconds) {
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  const m = Math.floor(totalSeconds / 60).toString();
  return `${m}:${s}`;
};

audioPlayer.toggleTrack = function(idx) {
  if (this.currentIndex === idx) {
    const track = this.tracks[idx];
    if (!track) return;
    if (track.audio.paused) {
      track.audio.play().catch(()=>{});
      track.li && track.li.classList.add('playing');
    } else {
      track.audio.pause();
      track.li && track.li.classList.remove('playing');
    }
    this.updateButtons();
    this.updateWaveformState();
  } else {
    this.playIndex(idx);
  }
};

audioPlayer.updateButtons = function() {
  this.tracks.forEach((t, idx) => {
    const btn = t.li && t.li.querySelector('.play-toggle');
    if (!btn) return;
    const isCurrent = idx === this.currentIndex;
    const isPlaying = isCurrent && !t.audio.paused && !t.audio.ended;
    btn.textContent = isPlaying ? '❚❚' : '▶';
    btn.setAttribute('aria-label', (isPlaying ? 'Pause' : 'Play') + ' track ' + (t.number));
  });
};

audioPlayer.updateWaveformState = function() {
  if (!this.$waveform) return;
  // Determine playing based on currentIndex and paused state
  const playing = this.currentIndex !== -1 && !this.tracks[this.currentIndex].audio.paused;
  this.$waveform.classList.toggle('playing', playing);
  this.$waveform.setAttribute('aria-pressed', playing ? 'true' : 'false');

  // Lottie animation control
  if (this.$lottiePlayer && this.lottieInstance) {
    if (playing) {
      // Load and loop WaveLoop_opt.json
      this.lottieInstance.destroy();
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: true,
        autoplay: true,
  path: './lottie/WaveLoop_opt.json',
      });
    } else {
      // Load and play WaveOff.json once
      this.lottieInstance.destroy();
      this.lottieInstance = lottie.loadAnimation({
        container: this.$lottiePlayer,
        renderer: 'svg',
        loop: false,
        autoplay: true,
  path: './lottie/WaveOff_opt.json',
      });
    }
  }
};

export default audioPlayer;